import React, { useLayoutEffect, useMemo, useRef } from 'react'
import { VariableSizeList, ListChildComponentProps } from 'react-window'
import styles from './styles.module.css'

type SelectionRow<T> = { key: string; item: T } | { key: string; label: string }

interface ISelectionListProps<T> {
  items: T[]
  getId: (item: T) => string
  selectedIds: string[]
  sections?: { selected: string; unselected: string }
  height: number
  direction: 'rtl' | 'ltr'
  renderItem: (item: T, style: React.CSSProperties) => React.ReactNode
}

interface IRowData<T> {
  rows: SelectionRow<T>[]
  renderItem: ISelectionListProps<T>['renderItem']
  rememberFocus: (key: string, target: HTMLElement) => void
}

function Row<T>({ index, style, data }: ListChildComponentProps<IRowData<T>>) {
  const row = data.rows[index]

  return 'item' in row ? (
    <div
      style={style}
      data-selection-item={row.key}
      onFocusCapture={(event) => data.rememberFocus(row.key, event.target as HTMLElement)}
    >
      {data.renderItem(row.item, { width: '100%', height: '100%' })}
    </div>
  ) : (
    <div role="heading" aria-level={3} style={style} className={styles.section}>
      {row.label}
    </div>
  )
}

// Shared by the user and tag selectors. Omit sections to preserve flat-list ordering.
export function SelectionList<T>({
  items,
  getId,
  selectedIds,
  sections,
  height,
  direction,
  renderItem,
}: ISelectionListProps<T>) {
  const listRef = useRef<VariableSizeList>(null)
  const outerRef = useRef<HTMLDivElement>(null)
  const focusedItem = useRef<{ key: string; target: HTMLElement }>()
  const selectedLabel = sections?.selected
  const unselectedLabel = sections?.unselected
  const rows = useMemo(() => {
    const itemRows = items.map((item) => ({ key: `item:${getId(item)}`, item }))
    if (selectedLabel === undefined || unselectedLabel === undefined) return itemRows

    const selectedSet = new Set(selectedIds)
    const selected: SelectionRow<T>[] = []
    const unselected: SelectionRow<T>[] = []
    itemRows.forEach((row) => (selectedSet.has(getId(row.item)) ? selected : unselected).push(row))

    return [
      ...(selected.length ? [{ key: 'selected', label: selectedLabel }, ...selected] : []),
      ...(unselected.length ? [{ key: 'unselected', label: unselectedLabel }, ...unselected] : []),
    ]
  }, [items, getId, selectedIds, selectedLabel, unselectedLabel])
  const data = {
    rows,
    renderItem,
    rememberFocus: (key: string, target: HTMLElement) => {
      focusedItem.current = { key, target }
    },
  }

  useLayoutEffect(() => {
    listRef.current?.resetAfterIndex(0)

    // A selected row can move out of the virtual window. Keep keyboard users on
    // that checkbox, without stealing focus from search or another control.
    const focused = focusedItem.current
    if (!focused || focused.target.isConnected || document.activeElement !== document.body) return

    const index = rows.findIndex((row) => row.key === focused.key)
    if (index < 0) return

    listRef.current?.scrollToItem(index, 'smart')
    const frame = requestAnimationFrame(() => {
      if (document.activeElement !== document.body) return

      const row = Array.from(outerRef.current?.querySelectorAll<HTMLElement>('[data-selection-item]') || []).find(
        (element) => element.dataset.selectionItem === focused.key
      )
      row?.querySelector<HTMLElement>('input')?.focus()
    })

    return () => cancelAnimationFrame(frame)
  }, [rows])

  return (
    <VariableSizeList<IRowData<T>>
      ref={listRef}
      outerRef={outerRef}
      direction={direction}
      height={height}
      width="100%"
      itemCount={rows.length}
      itemSize={(index) => ('item' in rows[index] ? 60 : 32)}
      itemKey={(index) => rows[index].key}
      itemData={data}
    >
      {Row}
    </VariableSizeList>
  )
}
